import { personName } from "@bt/domain";
import { useMutation, useQuery } from "@tanstack/react-query";
import { MailXIcon } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Field } from "@/components/app/field";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useMe } from "@/lib/account";
import { errorCode, errorMessage } from "@/lib/errors";
import { useFormat } from "@/lib/format";
import { type RouterOutputs, useTRPC } from "@/lib/trpc";
import { AuthCard } from "./account-app";
import { FormError, NewPasswordFields, newPasswordErrors } from "./sign-in";

type Invitation = RouterOutputs["invitations"]["get"];

/**
 * /account/invite/<token>: a new person chooses a password; someone with an account signs in, or
 * just accepts when already signed in as that account.
 */
export function InvitePage({ token }: { token: string }) {
  const { t } = useTranslation();
  const trpc = useTRPC();
  const {
    data: invitation,
    error,
    isPending,
  } = useQuery({
    ...trpc.invitations.get.queryOptions({ token }),
    retry: false,
  });
  if (isPending) return null;
  if (!invitation) {
    return (
      <AuthCard
        title={t("account.invalidTitle")}
        body={
          errorCode(error) === "INVITATION_INVALID"
            ? t("account.invalidBody")
            : errorMessage(error, t)
        }
      >
        <MailXIcon className="mx-auto size-8 text-muted-foreground" aria-hidden />
      </AuthCard>
    );
  }
  return <InvitationForm token={token} invitation={invitation} />;
}

function InvitationForm({ token, invitation }: { token: string; invitation: Invitation }) {
  const { t } = useTranslation();
  const trpc = useTRPC();
  const format = useFormat();
  const { data: me } = useMe();
  const accept = useMutation({
    ...trpc.invitations.accept.mutationOptions(),
    meta: { silent: true },
  });
  const [firstName, setFirstName] = useState(invitation.firstName);
  const [lastName, setLastName] = useState(invitation.lastName);
  const [password, setPassword] = useState("");
  const [repeat, setRepeat] = useState("");
  const [errors, setErrors] = useState<Record<string, string | undefined>>({});
  const [serverError, setServerError] = useState<string | null>(null);

  const signedInAsInvitee = invitation.accountExists && me?.email === invitation.email;
  const role = t(`roleInText.${invitation.role ?? "admin"}`);
  const title = invitation.garage
    ? t("account.inviteGarageTitle", { garage: invitation.garage })
    : t("account.inviteAdminTitle");
  const body = (
    <>
      <p>
        {invitation.invitedBy
          ? t("account.invitedBy", { inviter: invitation.invitedBy, role })
          : t("account.invitedAs", { role })}
      </p>
      <p className="mt-1">
        {t("account.validUntil", { date: format.dateTime(invitation.expiresAt) })}
      </p>
    </>
  );

  const submit = async () => {
    const problems: Record<string, string | undefined> = {};
    if (!invitation.accountExists) {
      for (const [field, value] of [
        ["firstName", firstName],
        ["lastName", lastName],
      ] as const) {
        const parsed = personName.safeParse(value);
        if (!parsed.success) problems[field] = parsed.error.issues[0]?.message;
      }
      Object.assign(problems, newPasswordErrors(password, repeat) ?? {});
    } else if (!signedInAsInvitee && !password) {
      problems.password = "validation.required";
    }
    setErrors(problems);
    if (Object.values(problems).some(Boolean)) return;
    setServerError(null);
    try {
      const result = await accept.mutateAsync({
        token,
        firstName: firstName.trim() || invitation.firstName || "-",
        lastName: lastName.trim() || invitation.lastName || "-",
        password: signedInAsInvitee ? null : password,
      });
      window.location.assign(result.garage ? `/${result.garage}/` : "/admin");
    } catch (error) {
      setServerError(errorMessage(error, t));
    }
  };

  return (
    <AuthCard title={title} body={body} wide>
      <form
        className="flex flex-col gap-4"
        onSubmit={(event) => {
          event.preventDefault();
          void submit();
        }}
      >
        {invitation.accountExists ? (
          <>
            <p className="text-sm">
              {signedInAsInvitee
                ? t("account.signedInAs", { email: invitation.email })
                : t("account.existingAccount", { email: invitation.email })}
            </p>
            {signedInAsInvitee ? null : (
              <>
                <Field label={t("account.email")} htmlFor="invite-email">
                  <Input
                    id="invite-email"
                    value={invitation.email}
                    readOnly
                    autoComplete="username"
                  />
                </Field>
                <Field
                  label={t("account.password")}
                  htmlFor="invite-password"
                  error={errors.password}
                >
                  <Input
                    id="invite-password"
                    type="password"
                    autoFocus
                    autoComplete="current-password"
                    value={password}
                    aria-invalid={errors.password ? true : undefined}
                    onChange={(e) => setPassword(e.target.value)}
                  />
                </Field>
              </>
            )}
          </>
        ) : (
          <>
            <Field label={t("account.email")} htmlFor="invite-email">
              <Input id="invite-email" value={invitation.email} readOnly autoComplete="username" />
            </Field>
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label={t("account.firstName")} htmlFor="invite-first" error={errors.firstName}>
                <Input
                  id="invite-first"
                  autoComplete="given-name"
                  value={firstName}
                  aria-invalid={errors.firstName ? true : undefined}
                  onChange={(e) => setFirstName(e.target.value)}
                />
              </Field>
              <Field label={t("account.lastName")} htmlFor="invite-last" error={errors.lastName}>
                <Input
                  id="invite-last"
                  autoComplete="family-name"
                  value={lastName}
                  aria-invalid={errors.lastName ? true : undefined}
                  onChange={(e) => setLastName(e.target.value)}
                />
              </Field>
            </div>
            <NewPasswordFields
              idPrefix="invite"
              label={t("account.password")}
              password={password}
              repeat={repeat}
              onPassword={setPassword}
              onRepeat={setRepeat}
              errors={errors}
            />
          </>
        )}
        <FormError message={serverError} />
        <Button type="submit" size="lg" disabled={accept.isPending}>
          {invitation.accountExists && !signedInAsInvitee
            ? t("account.signInAndAccept")
            : t("account.accept")}
        </Button>
      </form>
    </AuthCard>
  );
}
